/** Разбор заявки (поставки) — общий для раскрытой строки вкладки «Поставки» и карточки поставки
 *  (боковая панель): что не так (диагноз по актам), поставки заявки, акт приёмки по артикулам,
 *  начисления по месяцам (`GET /supplies/{order_number}`). */
import React from "react";

import type { ActItem, OzSupplyDetail, SupplyDiag } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { int, monthLabel, rub } from "./format";
import { IssueList } from "./SupplyIssues";
import { moneyClass } from "./ui";

/** «Что не так с поставкой»: замечания всех поставок заявки + строка на поставку (статус, склад →
 *  кластер, грузоместа). Нет строк диагноза — заявки нет в ozon_fin_supply_diag. */
export function SupplyDiagSection({ supplies }: { supplies: SupplyDiag[] }): React.ReactElement {
  const issues = supplies.flatMap((s) =>
    (s.issues ?? []).map((i) => ({ ...i, supply_id: s.supply_id })),
  );
  return (
    <section>
      <h3 className="mb-1 text-sm font-semibold">Что не так с поставкой</h3>
      {supplies.length > 0 ? (
        <IssueList issues={issues} />
      ) : (
        <p className="text-xs text-muted-foreground">
          Диагноза нет: заявки нет в ozon_fin_supply_diag — есть только начисления.
        </p>
      )}
      {supplies.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
          {supplies.map((s) => (
            <span key={String(s.supply_id)}>
              поставка {s.supply_id ?? "—"} · {s.state ?? "—"} ·{" "}
              {[s.dropoff, s.cluster && `→ ${s.cluster}`].filter(Boolean).join(" ") || "—"}
              {s.cargo &&
                ` · ГМ: ${Object.entries(s.cargo)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(", ")}`}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/** «Акт приёмки по артикулам». `compact` — боковая панель: без колонки названия (оно — в
 *  подсказке артикула), причины брака обрезаются. */
export function SupplyActSection({
  items,
  compact = false,
}: {
  items: ActItem[];
  compact?: boolean;
}): React.ReactElement {
  return (
    <section>
      <h3 className="mb-1 text-sm font-semibold">Акт приёмки по артикулам</h3>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Акта по SKU нет — запрашивается только для поставок с расхождениями или штрафами.
        </p>
      ) : (
        <ActTable items={items} compact={compact} />
      )}
    </section>
  );
}

/** «Начисления по месяцам» заявки — за всё время (кросс-док и штрафы приходят позже отгрузки). */
export function SupplyAccrualsSection({ detail }: { detail: OzSupplyDetail }): React.ReactElement {
  return (
    <section>
      <h3 className="mb-1 text-sm font-semibold">
        Начисления по месяцам · всего{" "}
        <span className={cn("tabular-nums", moneyClass(detail.accrual_total))}>
          {rub(detail.accrual_total)}
        </span>
      </h3>
      {detail.accruals.length === 0 ? (
        <p className="text-xs text-muted-foreground">Начислений по заявке нет.</p>
      ) : (
        <table className="text-xs">
          <tbody>
            {detail.accruals.map((a, i) => (
              <tr key={i}>
                <td className="pr-4 tabular-nums">{monthLabel(a.month)}</td>
                <td className="pr-4 text-muted-foreground">{a.grp_label}</td>
                <td className="pr-4">{a.item}</td>
                <td className={cn("text-right tabular-nums", moneyClass(a.amount))}>
                  {rub(a.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function reasons(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join("; ");
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function ActTable({ items, compact }: { items: ActItem[]; compact: boolean }): React.ReactElement {
  const n = (v: number | null): string => (v === null ? "—" : int(v));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="act-table">
        <thead>
          <tr className="text-xs text-muted-foreground">
            <th className="px-2 text-left font-medium">Поставка</th>
            <th className="px-2 text-left font-medium">Артикул</th>
            {!compact && <th className="px-2 text-left font-medium">Название</th>}
            <th className="px-2 text-right font-medium">Заявлено</th>
            <th className="px-2 text-right font-medium">Факт</th>
            <th className="px-2 text-right font-medium">Принято</th>
            <th className="px-2 text-right font-medium">Брак</th>
            <th className="px-2 text-left font-medium">Причины брака</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a, i) => {
            const diff = a.declared !== null && a.fact !== null && a.declared !== a.fact;
            const why = reasons(a.defect_reasons);
            return (
              <tr key={i} className="border-t">
                <td className="px-2 py-1 tabular-nums text-muted-foreground">
                  {a.supply_id}
                  {a.act_type ? ` · ${a.act_type}` : ""}
                </td>
                <td className="px-2 py-1" title={compact ? (a.name ?? undefined) : undefined}>
                  {a.offer_id ?? a.sku ?? "—"}
                </td>
                {!compact && (
                  <td className="max-w-[320px] truncate px-2 py-1" title={a.name ?? undefined}>
                    {a.name ?? "—"}
                  </td>
                )}
                <td className="px-2 py-1 text-right tabular-nums">{n(a.declared)}</td>
                <td
                  className={cn(
                    "px-2 py-1 text-right tabular-nums",
                    diff && "font-medium text-red-600 dark:text-red-400",
                  )}
                >
                  {n(a.fact)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{n(a.approved)}</td>
                <td
                  className={cn(
                    "px-2 py-1 text-right tabular-nums",
                    (a.defect ?? 0) > 0 && "font-medium text-red-600 dark:text-red-400",
                  )}
                >
                  {n(a.defect)}
                </td>
                <td
                  className={cn(
                    "px-2 py-1 text-xs text-muted-foreground",
                    compact && "max-w-[180px] truncate",
                  )}
                  title={compact && why ? why : undefined}
                >
                  {why}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
